import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import clsx from 'clsx';
import { Button, IconButton, Modal, Tooltip } from '@material-ui/core';

import {
  CustomDatePicker,
  CustomSelect,
  ValidatingTextField,
  EllipsedText,
} from '../FormComponents';

import { API } from '../../apis/api';
import {
  ADD_COLOUR_OR_COUNTRY,
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
} from '../../constants';

import {
  isPositiveInteger,
  isQuantityValid,
} from '../../utils/utils';

import { reasons, agencies } from '../../staticData';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';

import styles from './AddVcFormProlife.module.css';
import AddCountryOrColourForm from '../AddCountryOrColourForm/AddCountryOrColourForm';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { buildErrorMessage } from '../../apis/calls';
import { useErrorHandler } from 'react-error-boundary';
import { getSequenceStartDateString } from '../../utils/tableUtils';
import { useRef } from 'react';

/* ---------------- CONFIG HELPERS ---------------- */

const validators = {
  positiveInteger: isPositiveInteger,
  quantity: isQuantityValid,
};

const formatOptions = arr =>
  arr.map(v => ({ value: v, label: v }));

/* ---------------- COMPONENT ---------------- */

const AddVcFormProlife = ({
  open,
  handleClose,
  plant,
  plantName,
  line,
  lineName,
  lastAsnGeneratedDate,
  updatePlanRows,
  vcPlanData,
  skipToLastPage,
  config,
}) => {
  const [showCountryPopup, setShowCountryPopup] = useState(false);
  const [showColourPopup, setShowColourPopup] = useState(false);
  const isLucknowProlifePlant = plantName === 'Lucknow-Prolife'
  /* ---------- STATE ---------- */
  const { showPopup } = usePopupManager();
  const handleError = useErrorHandler();

  const YES = 'yes';
  const NO = 'no';

  const formContainerRef = useRef(null);

  const [vc, setVc] = useState('');
  const [market, setMarket] = useState('');
  const [category, setCategory] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [defence, setDefence] = useState('');
  const [fbv, setFbv] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [dayTarget, setDayTarget] = useState('');
  const [evrNumber, setEvrNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [reason, setReason] = useState('');
  const [agency, setAgency] = useState('');
  const [comment, setComment] = useState('');
  const [buildCardNumber, setBuildCardNumber] = useState(null);
  const [powerSteering, setPowerSteering] = useState(null);
  const [colour, setColour] = useState('');

  const [vcs, setVcs] = useState({ loading: false, data: [] });
  const [displayVcCombinationNotUniqueError, setDisplayVcCombinationNotUniqueError] = useState(false);
  const [highlightMandatoryFields, setHighlightMandatoryFields] = useState(
    false,
  );
  const [countries, setCountries] = useState({ loading: false, data: [] });
  const [colours, setColours] = useState({ loading: false, data: [] });
  const [EvrOptions, setEvrOptions] = useState([])
  const [evrNumberBalance, setEvrNumberBalance] = useState(null);

  const existingPlannedVcCombinations = vcPlanData.map(
    ({
      vc,
      market,
      category,
      countryCode,
      defence,
      plan
    }) => ({
      vc,
      market,
      category,
      countryCode,
      defence,
      plan
    }),
  );

  const parentName =
    plantName === 'Lucknow-Prolife' ? 'Part' : 'VC';

  /* ---------- LOADERS ---------- */

  const getEvrBalance = (evrNumber) => {
    const selectedOption = EvrOptions.find(option => option.value === evrNumber);
    return selectedOption ? selectedOption.balenceQuantity : 0;
  };

  const handleSelectionCurryEvr = setState => valueObject => {
    setState(valueObject);
    const balance = getEvrBalance(valueObject.value);
    setEvrNumberBalance(balance);
  };

  const loadData = async (setState, url, params) => {
    try {
      const response = await API.get(url, {
        params: params,
      });
      setState({ loading: false, data: response.data });
    } catch (error) {
      console.error(buildErrorMessage(error));
      handleError(error);
    }
  };

  const loadVcs = async () => {
    return loadData(setVcs, API_RESOURCE_URLS.VC_DESCRIPTION, {
      line,
      plant,
    });
  };

  const getCountryCodeOptions = () => {
    return countries.data
      .map(({ code, country }) => ({
        value: code,
        label: `${code} - ${country}`,
      }))
      .sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
  };

  const loadEvrNumber = async () => {
    try {
      const country = countryCode.label.split(" - ")[1];
      const response = await API.get(API_RESOURCE_URLS.getEvrNumberOptions(plant), {
        params: {
          vc: vc.value,
          plannedDate: startDate,
          countryCode: countryCode.value,
          country: country,
          line: line,
        }
      });
      const formattedOptions = response.data.map(({ evrNumber, balenceQuantity }) => ({
        label: evrNumber,
        value: evrNumber,
        balenceQuantity
      }));
      setEvrOptions(formattedOptions);
    } catch (error) {
      console.error('Error fetching EVR options:', error);
    }
  };

  const loadCountries = async () => {
    return loadData(setCountries, API_RESOURCE_URLS.COUNTRIES);
  };

  const isEvr = () => {
    return market?.value !== 'E';
  }
  const getColourOptions = () => {
    return colours.data.map(({ colourCode, colourDescription }) => ({
      value: colourDescription + ' (' + colourCode + ')',
      label: colourDescription + ' (' + colourCode + ')',
    }));
  };
  /* ---------- HANDLERS ---------- */

  const checkIfVcCombinationAlreadyExists = (
    newVcCombination,
    existingVcCombinations,
  ) => {

    return existingVcCombinations.some(vcCombination => {
      const isSameCombination =
        vcCombination.vc === newVcCombination.vc &&
        vcCombination.market === newVcCombination.market &&
        vcCombination.category === newVcCombination.category &&
        vcCombination.countryCode === newVcCombination.countryCode &&
        vcCombination.defence === newVcCombination.defence &&
        vcCombination.evrNumber === newVcCombination.evrNumber;
      const isEditedForCurrentDate =
        Boolean(vcCombination.plan?.[startDate]) &&
        vcCombination.plan[startDate].edited === true;

      return isSameCombination && isEditedForCurrentDate;
    }
    );
  };

  const validateFields = fields => {
    const {
      fbvVendorCode,
      buildCardNumber,
      powerSteering,
      ...requiredFields
    } = fields;
    const hasMissingRequiredFields = Object.values(requiredFields).some(
      field => !field,
    );
    if (hasMissingRequiredFields) {
      return false;
    }
    const { dayTarget } = fields;
    if (!isPositiveInteger(dayTarget)) return false;
    if (!isQuantityValid(dayTarget)) return false;
    return true;
  };

  const getSelectedVcCombination = () => ({
    category: category && category.value,
    countryCode: countryCode && countryCode.value,
    defence: defence && defence.value,
    market: market && market.value,
    vc: vc && vc.value,
    ...(market?.value === "E" && { evrNumber: evrNumber && evrNumber.value }),
  });

  const scrollFormToTop = () => {
    formContainerRef.current.scrollTop = 0;
  };
  const runPreSubmissionChecks = () => {
    const vcCombination = getSelectedVcCombination();
    const vcCombinationAlreadyExists = checkIfVcCombinationAlreadyExists(
      vcCombination,
      existingPlannedVcCombinations,
    );
    if (vcCombinationAlreadyExists) {
      scrollFormToTop();
      setDisplayVcCombinationNotUniqueError(true);
      return false;
    }

    const areFieldsValid = validateFields({
      ...vcCombination,
      reason,
      agency,
      dayTarget,
      startDate,
    });

    if (!areFieldsValid) {
      setHighlightMandatoryFields(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    const vcCombination = getSelectedVcCombination();
    try {
      await API.put(`${API_RESOURCE_URLS.VC_QUANTITY}`, {
        plant,
        line,
        ...vcCombination,
        quantities: [{ date: startDate, quantity: Number(dayTarget) }],
        reason: reason.value,
        agency: agency.value,
        evrNumber: evrNumber ? evrNumber.value : null,
        comment,
      });
      handleClose();
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: `${parentName} added.`,
        info: `${parentName} no: ${vc.label}`,
      });
      await updatePlanRows();
      skipToLastPage();
    } catch (error) {
      console.error(buildErrorMessage(error));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        contextText: isLucknowProlifePlant ? DISPLAY_MESSAGES.PART_ADD_FAILURE : DISPLAY_MESSAGES.VC_ADD_FAILURE,
        info: 'Plan Revision failed.',
        error: error,
      });
    }
  };

  /* ---------- OPTIONS ---------- */

  const vcOptions = formatOptions(vcs.data?.map(v => v?.vc));
  const marketOptions = [
    { value: 'E', label: 'Export' },
    { value: 'D', label: 'Domestic' },
  ];
  const categoryOptions = formatOptions(['MTO', 'MTS']);
  const yesAndNoOptions = [
    { value: YES, label: 'Yes' },
    { value: NO, label: 'No' },
  ];

  const reasonOptions = formatOptions(reasons);
  const agencyOptions = formatOptions(agencies);
  const handleSelectionCurry = setState => valueObject => setState(valueObject);

  const loadColours = async () => {
    return loadData(setColours, API_RESOURCE_URLS.COLOURS);
  };

  const resetState = async () => {
    setVc(null);
    setMarket(null);
    setCategory(null);
    setCountryCode(null);
    setDefence(null);
    setDayTarget(null);
    setEvrNumber(null);
    setStartDate(null);
    setReason(null);
    setAgency(null);
    setComment(null);
    setDisplayVcCombinationNotUniqueError(null);
    setHighlightMandatoryFields(null);
    setEvrNumberBalance(null);
  };

  useEffect(() => {
    if (open) {
      loadEvrNumber();
      loadVcs();
      loadCountries();
    } else {
      resetState();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plant, line, showColourPopup, showCountryPopup]);

  useEffect(() => {
    if (vc && startDate && countryCode) {
      loadEvrNumber(vc, startDate, countryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vc, startDate, countryCode]);
  /* ---------- RENDER ---------- */

  return (
    <Modal open={open} onClose={handleClose} disableBackdropClick>
      <div className={styles.container} ref={formContainerRef}>
        <div className={styles.header}>
          Plan Revision: Add a {parentName}
          <span className={styles.meta}>
            Line <b>{lineName}</b> | Plant <b>{plantName}</b>
          </span>
        </div>

        {/* ===== BASIC INFO ===== */}
        <section className={styles.section}>
          <span className={styles.sectionTitle}>Basic Info</span>

          <div className={styles.basicGrid}>
            {config.fields.vc?.enabled && (
              <Field label={`${parentName} Number`} required>
                <CustomSelect
                  name="part-number"
                  options={vcOptions}
                  value={vc}
                  isMandatory
                  onChange={handleSelectionCurry(setVc)}
                  markIfUnselected={highlightMandatoryFields}
                  showError={displayVcCombinationNotUniqueError}
                  maxMenuHeight={300}
                  className={styles.select}
                />
                <span className={clsx(styles.errorText)}>
                  {displayVcCombinationNotUniqueError
                    ? isLucknowProlifePlant
                      ? DISPLAY_MESSAGES.DUPLICATE_PART_COMBO_WARNING
                      : DISPLAY_MESSAGES.DUPLICATE_VC_COMBO_WARNING
                    : ""
                  }
                </span>
              </Field>
            )}

            <Field label="Description" required>
              <EllipsedText
                value={
                  vcs?.data?.find(v => v.vc === vc?.value)?.description ?? '--'
                }
                className={styles.description}
              />
            </Field>
          </div>
        </section>

        {/* ===== VC / PART + PLAN DETAILS ===== */}
        <section className={styles.section}>
          <div className={styles.splitGrid}>

            {/* LEFT – VC DETAILS (2 cols) */}
            <div>
              <span className={styles.sectionTitle}>{parentName} Details</span>

              <div className={styles.vcGrid}>
                {config.fields.market?.enabled && (
                  <Field label="Market" required>
                    <CustomSelect
                      name="market"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={marketOptions}
                      className={styles.select}
                      value={market}
                      onChange={handleSelectionCurry(setMarket)}
                    />
                  </Field>
                )}

                {config.fields.countryCode?.enabled && (
                  <Field
                    required
                    label={
                      <div className={styles.labelWithIcon}>
                        Country
                        <Tooltip title="Add a new country" arrow>
                          <IconButton
                            className={styles.addButton}
                            onClick={() => setShowCountryPopup(true)}
                            data-testid="add-country-button"
                            size="small"
                          >
                            <AddCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    }
                  >
                    <CustomSelect
                      name="country"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={getCountryCodeOptions()}
                      className={styles.select}
                      value={countryCode}
                      onChange={handleSelectionCurry(setCountryCode)}
                    />

                    <AddCountryOrColourForm
                      open={showCountryPopup}
                      handleClose={() => setShowCountryPopup(false)}
                      formType={ADD_COLOUR_OR_COUNTRY.COUNTRY}
                    />
                  </Field>
                )}

                {config.fields.category?.enabled && (
                  <Field label="Category" required>
                    <CustomSelect
                      name="category"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={categoryOptions}
                      className={styles.select}
                      value={category}
                      onChange={handleSelectionCurry(setCategory)}
                    />
                  </Field>
                )}

                {config.fields.defence?.enabled && (
                  <Field label="Defence" required>
                    <CustomSelect
                      name="defence"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={yesAndNoOptions}
                      className={styles.select}
                      value={defence}
                      onChange={handleSelectionCurry(setDefence)}
                    />
                  </Field>
                )}

                {config.fields.buildCardNumber?.enabled && (
                  <Field label="BC No.">
                    <ValidatingTextField className={styles.textField} />
                  </Field>
                )}

                {config.fields.fbv?.enabled && (
                  <Field label="FBV">
                    <CustomSelect className={styles.selectSmall} />
                  </Field>
                )}

                {config.fields.vendorName?.enabled && (
                  <Field label="Vendor Name">
                    <CustomSelect className={styles.selectSmall} />
                  </Field>
                )}

                {config.fields.vendorCode?.enabled && (
                  <Field label="Vendor Code">
                    <CustomSelect className={styles.selectSmall} />
                  </Field>
                )}

                {config.fields.powerSteering?.enabled && (
                  <Field label="P.Steering **">
                    <CustomSelect className={styles.selectSmall} />
                  </Field>
                )}

                {config.fields.colour?.enabled && (
                  <div className={styles.col5}>
                    <label className={styles.label}>
                      Colour
                      <Tooltip title="Add a new colour" arrow>
                        <Button
                          startIcon={<AddCircleIcon />}
                          className={styles.addButton}
                          onClick={() => setShowColourPopup(true)}
                          data-testid='add-colour-button'
                        >
                        </Button>
                      </Tooltip>
                      <AddCountryOrColourForm
                        open={showColourPopup}
                        handleClose={() => setShowColourPopup(false)}
                        formType={ADD_COLOUR_OR_COUNTRY.COLOUR}
                      />
                    </label>
                    <CustomSelect
                      name="colour"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={getColourOptions()}
                      className={styles.selectSmall}
                      value={colour}
                      onChange={handleSelectionCurry(setColour)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT – PLAN DETAILS (1 col) */}
            <div>
              <span className={styles.sectionTitle}>Plan Details</span>

              <div className={styles.planGrid}>
                {config.fields.startDate?.enabled && (
                  <Field label="Start Date" required>
                    <CustomDatePicker
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      autoOk
                      inputVariant="outlined"
                      value={startDate}
                      onChange={date => {
                        setStartDate(date.format(DATE_FORMAT.ISO));
                      }}
                      inputProps={{
                        className: clsx(styles.select, styles.dateInput),
                      }}
                      emptyLabel="dd/mm/yyyy"
                      minDate={
                        process.env.NODE_ENV === 'test'
                          ? moment(1)
                          : getSequenceStartDateString(lastAsnGeneratedDate)
                      }
                      format="DD/MM/yyyy"
                      data-testid="date-picker-div"
                    />
                  </Field>
                )}

                {config.fields.dayTarget?.enabled && (
                  <Field label="Day Target" required>
                    <ValidatingTextField
                      isMandatory
                      validationFn={isPositiveInteger}
                      markIfEmpty={highlightMandatoryFields}
                      isQuantityValid={isQuantityValid(dayTarget)}
                      validationHelperText={
                        DISPLAY_MESSAGES.POSITIVE_NUMBER_VALIDATION_WARNING
                      }
                      quantityValidationText={DISPLAY_MESSAGES.INVALID_INPUT}
                      variant="outlined"
                      value={dayTarget}
                      onChange={e => setDayTarget(e.target.value)}
                      placeholder="(Enter Target)"
                      className={styles.textField}
                      inputProps={{
                        className: styles.textInput,
                        'data-testid': 'target-number-input',
                      }}
                      FormHelperTextProps={{
                        className: styles.helperText,
                      }}
                    />
                  </Field>
                )}

                {config.fields.evrNumberBalance?.enabled && (
                  <Field label="EVR/DVR No." required>
                    <CustomSelect
                      name="EVR/DVR"
                      isMandatory
                      isDisabled={isEvr()}
                      markIfUnselected={highlightMandatoryFields}
                      options={EvrOptions}
                      className={styles.selectSmall}
                      value={evrNumber}
                      onChange={handleSelectionCurryEvr(setEvrNumber)}
                      data-testid='evr-number-dropdown'
                    />
                  </Field>
                )}

                {config.fields.evrNumber?.enabled && (
                  <Field label="EVR Balance" required>
                    <ValidatingTextField
                      disabled
                      variant="outlined"
                      value={evrNumberBalance}
                      isQuantityValid
                      className={styles.newText}
                      inputProps={{
                        className: styles.newTextInput,
                        'data-testid': 'evr-balance-input',
                      }}
                      FormHelperTextProps={{
                        className: styles.helperText,
                      }}
                    />
                  </Field>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ===== REVISION DETAILS ===== */}
        <section className={styles.section}>
          <span className={styles.sectionTitle}>Revision Details</span>

          <div className={styles.splitGrid}>
            <div className={styles.revisionGrid}>
              {config.fields.reason?.enabled && (
                <Field label="Reason" required>
                  <CustomSelect
                    name="reason"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    options={reasonOptions}
                    className={styles.select}
                    value={reason}
                    onChange={handleSelectionCurry(setReason)}
                  />
                </Field>
              )}



              {config.fields.agency?.enabled && (
                <Field label="Agency" required>
                  <CustomSelect
                    name="agency"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    options={agencyOptions}
                    className={styles.select}
                    value={agency}
                    onChange={handleSelectionCurry(setAgency)}
                  />
                </Field>
              )}

            </div>

            {config.fields.comment?.enabled && (
              <Field
                label={
                  <div className={styles.labelStack}>
                    <span>Comments</span>
                    <span className={styles.optionalText}>(Optional)</span>
                  </div>
                }
                required
              >
                <textarea
                  className={styles.comments}
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                ></textarea>
              </Field>
            )}
          </div>

        </section>

        <div className={styles.actions}>
          <button onClick={handleClose}>Cancel</button>
          <button onClick={e => {
            if (e.detail > 1) {
              console.log('A double-click has been registered');
              return;
            }
            return runPreSubmissionChecks() && handleSubmit();
          }}>Submit</button>
        </div>
      </div>

    </Modal>
  );
};

/* ---------- SMALL HELPER ---------- */

const Field = ({ label, required, children }) => (
  <div className={styles.field}>
    <label className={styles.fieldLabel}>
      {label} {!required && <span>**</span>}
    </label>
    <div className={styles.fieldInput}>
      {children}
    </div>
  </div>
);

AddVcFormProlife.propTypes = {
  config: PropTypes.object.isRequired,
};

export default AddVcFormProlife;
